'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useNavigate, useParams } from 'react-router-dom'
import {
    createRoom,
    updateRoom,
    getRoom,
    ROOM_COLORS,
} from '@/lib/api/rooms/rooms-api'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdMeetingRoom } from 'react-icons/md'

const CAPACITY_OPTIONS = [
    { value: 1, label: '1 pessoa' },
    { value: 2, label: '2 pessoas' },
    { value: 3, label: '3 pessoas' },
    { value: 4, label: '4 pessoas' },
    { value: 5, label: '5 pessoas' },
    { value: 6, label: '6 pessoas' },
    { value: 8, label: '8 pessoas' },
    { value: 10, label: '10 pessoas' },
    { value: 15, label: '15 pessoas' },
    { value: 20, label: '20 pessoas' },
]

export default function RoomFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams()
    const isEdit = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(isEdit)
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        capacity: '',
        resources: '',
        color: '#3B82F6',
        active: true,
    })

    useEffect(() => {
        if (isEdit && id) {
            loadRoom(parseInt(id))
        }
    }, [id, isEdit])

    const loadRoom = async (roomId: number) => {
        setLoadingData(true)
        try {
            const response = await getRoom(roomId)
            if (response.success) {
                const room = response.result
                setFormData({
                    name: room.name,
                    description: room.description || '',
                    capacity: room.capacity?.toString() || '',
                    resources: room.resources || '',
                    color: room.color || '#3B82F6',
                    active: room.active,
                })
            } else {
                toast.error(response.error || 'Erro ao carregar sala')
                navigate('/registrations/rooms')
            }
        } catch (error) {
            toast.error('Erro ao carregar sala')
            navigate('/registrations/rooms')
        } finally {
            setLoadingData(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }

        if (!user?.companyId) {
            toast.error('Empresa não identificada')
            return
        }

        setLoading(true)
        try {
            const data = {
                companyId: user.companyId,
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                capacity: formData.capacity
                    ? parseInt(formData.capacity)
                    : undefined,
                resources: formData.resources.trim() || undefined,
                color: formData.color || undefined,
                active: formData.active,
            }

            const response = isEdit
                ? await updateRoom(parseInt(id!), data)
                : await createRoom(data)

            if (response.success) {
                toast.success(
                    isEdit
                        ? 'Sala atualizada com sucesso'
                        : 'Sala criada com sucesso'
                )
                navigate('/registrations/rooms')
            } else {
                toast.error(response.error || 'Erro ao salvar sala')
            }
        } catch (error) {
            toast.error('Erro ao salvar sala')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando sala...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/registrations/rooms')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdMeetingRoom className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEdit ? 'Editar Sala' : 'Nova Sala'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEdit
                                ? 'Atualize os dados da sala'
                                : 'Adicione uma nova sala ou consultório'}
                        </p>
                    </div>
                </div>
            </div>

            <form
                onSubmit={handleSubmit}
                className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-control md:col-span-2">
                        <label className="label">
                            <span className="label-text font-medium">
                                Nome da Sala *
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Ex: Consultório 1, Sala de Cirurgia..."
                            value={formData.name}
                            onChange={e =>
                                setFormData({ ...formData, name: e.target.value })
                            }
                            required
                        />
                    </div>

                    <div className="form-control md:col-span-2">
                        <label className="label">
                            <span className="label-text font-medium">
                                Descrição
                            </span>
                        </label>
                        <textarea
                            className="textarea textarea-bordered w-full"
                            placeholder="Descreva a sala, localização, etc..."
                            rows={3}
                            value={formData.description}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    description: e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">
                                Capacidade
                            </span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={formData.capacity}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    capacity: e.target.value,
                                })
                            }
                        >
                            <option value="">Selecione...</option>
                            {CAPACITY_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">
                                Recursos/Equipamentos
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Ex: Ar-condicionado, Maca, Computador..."
                            value={formData.resources}
                            onChange={e =>
                                setFormData({
                                    ...formData,
                                    resources: e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">
                                Cor de Identificação
                            </span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {ROOM_COLORS.map(color => (
                                <button
                                    key={color.value}
                                    type="button"
                                    className={`w-8 h-8 rounded-full ${color.class} transition-all ${formData.color === color.value
                                        ? 'ring-2 ring-offset-2 ring-secondary scale-110'
                                        : 'hover:scale-105'
                                        }`}
                                    title={color.label}
                                    onClick={() =>
                                        setFormData({
                                            ...formData,
                                            color: color.value,
                                        })
                                    }
                                />
                            ))}
                        </div>
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">Status</span>
                        </label>
                        <label className="cursor-pointer flex items-center gap-3">
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary"
                                checked={formData.active}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        active: e.target.checked,
                                    })
                                }
                            />
                            <span className="label-text">
                                {formData.active ? 'Ativa' : 'Inativa'}
                            </span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-base-300/50">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/registrations/rooms')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-xl" />
                                {isEdit ? 'Atualizar' : 'Criar'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
